import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
];

function getNext7Days() {
  const days = [];
  const now = new Date();
  for (let i = 1; i <= 7; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    if (d.getDay() !== 0) { // skip Sundays
      days.push({
        date: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayName: d.toLocaleDateString('en-US', { weekday: 'long' }),
      });
    }
  }
  return days;
}

export default function BookCall() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', phone: '', date: '', time: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const days = getNext7Days();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error('Please fill in all fields.'); return; }
    if (!form.date || !form.time) { toast.error('Please pick a date and time.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('gym_name', `Booking: ${form.name}`);
      fd.append('contact_name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('notes', `Booked call: ${form.date} at ${form.time}`);
      fd.append('biggest_bottleneck', 'booking_call');
      fd.append('gym_type', '');
      fd.append('member_count', '');
      ['city_state','current_website','current_crm','lead_volume','membership_pricing','services_offered'].forEach(k => fd.append(k, ''));
      const res = await fetch(`${API}/api/huscleos/leads`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch { toast.error('Something went wrong.'); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0F0F0F', fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: '#FF2E00' }}>
            <CheckCircle2 size={32} color="#fff" />
          </div>
          <h1 className="text-3xl tracking-tighter uppercase mb-4" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>YOU'RE BOOKED.</h1>
          <p className="text-sm mb-2 text-gray-400">{form.dayName || form.date} at {form.time}</p>
          <p className="text-sm text-gray-500 mb-6">We'll send a confirmation to {form.email} with meeting details.</p>
          <Link to="/huscleos" className="inline-flex items-center gap-2 text-sm font-bold text-[#FF2E00] hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back to HuscleOS
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0F0F0F', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: '#2A2A2A' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-white transition-colors" data-testid="book-back">
            <ArrowLeft size={16} /> HuscleOS
          </Link>
          <div className="flex items-center gap-0">
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#fff' }}>HUSCLE</span>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: '#FF2E00' }}>BOOK A CALL</p>
          <h1 className="text-3xl sm:text-4xl tracking-tighter uppercase" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>
            {step === 1 ? 'PICK A TIME.' : 'YOUR INFO.'}
          </h1>
          <p className="text-sm mt-2 text-gray-500">
            {step === 1 ? '15 minutes. We\'ll walk through your gym and show you HuscleOS.' : 'So we can send you the meeting link.'}
          </p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          <div className="flex-1 h-1 rounded-full" style={{ background: '#FF2E00' }} />
          <div className="flex-1 h-1 rounded-full" style={{ background: step >= 2 ? '#FF2E00' : '#2A2A2A' }} />
        </div>

        {step === 1 && (
          <div>
            <p className="text-xs font-bold tracking-wide uppercase mb-4 text-gray-500">Select a day</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8">
              {days.map(d => (
                <button key={d.date} onClick={() => setForm(f => ({ ...f, date: d.date, dayName: d.dayName }))}
                  data-testid={`book-date-${d.date}`}
                  className={`p-3 border text-center transition-all ${form.date === d.date ? 'border-[#FF2E00]' : 'border-[#2A2A2A] hover:border-[#444]'}`}
                  style={{ background: form.date === d.date ? '#1E1008' : '#1A1A1A' }}>
                  <p className="text-xs text-gray-500">{d.dayName}</p>
                  <p className="text-sm font-bold text-white">{d.label.split(', ')[0]?.split(' ').slice(1).join(' ') || d.label}</p>
                </button>
              ))}
            </div>
            {form.date && (
              <>
                <p className="text-xs font-bold tracking-wide uppercase mb-4 text-gray-500">Select a time</p>
                <div className="grid grid-cols-3 gap-2 mb-8">
                  {TIME_SLOTS.map(t => (
                    <button key={t} onClick={() => setForm(f => ({ ...f, time: t }))}
                      data-testid={`book-time-${t.replace(/[: ]/g, '')}`}
                      className={`p-3 border text-center text-sm font-medium transition-all ${form.time === t ? 'border-[#FF2E00] text-white' : 'border-[#2A2A2A] text-gray-400 hover:border-[#444]'}`}
                      style={{ background: form.time === t ? '#1E1008' : '#1A1A1A' }}>
                      {t}
                    </button>
                  ))}
                </div>
              </>
            )}
            {form.date && form.time && (
              <button onClick={() => setStep(2)} data-testid="book-next-button"
                className="w-full flex items-center justify-center gap-2 text-sm font-bold tracking-widest uppercase py-4 transition-all hover:opacity-90"
                style={{ background: '#FF2E00', color: '#fff' }}>
                NEXT <ArrowRight size={16} />
              </button>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="p-4 border mb-4" style={{ borderColor: '#2A2A2A', background: '#1A1A1A' }}>
              <div className="flex items-center gap-3">
                <Calendar size={16} style={{ color: '#FF2E00' }} />
                <span className="text-sm text-white font-medium">{form.dayName}, {form.date}</span>
                <Clock size={16} style={{ color: '#FF2E00' }} className="ml-2" />
                <span className="text-sm text-white font-medium">{form.time}</span>
              </div>
            </div>
            {[{ key: 'name', ph: 'Your Name *' }, { key: 'email', ph: 'Email *', type: 'email' }, { key: 'phone', ph: 'Phone *', type: 'tel' }].map(({ key, ph, type }) => (
              <input key={key} type={type || 'text'} placeholder={ph} value={form[key]}
                onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
                data-testid={`book-${key}`}
                className="w-full p-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF2E00] focus:ring-1 focus:ring-[#FF2E00] transition-colors"
                style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }} />
            ))}
            <button onClick={handleSubmit} disabled={submitting} data-testid="book-submit-button"
              className="w-full flex items-center justify-center gap-2 text-sm font-bold tracking-widest uppercase py-4 transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: '#FF2E00', color: '#fff' }}>
              {submitting ? 'BOOKING...' : 'CONFIRM MY CALL'} {!submitting && <CheckCircle2 size={16} />}
            </button>
            <button onClick={() => setStep(1)} className="flex items-center gap-1.5 mt-2 text-sm text-gray-500 hover:text-white transition-colors">
              <ArrowLeft size={14} /> Change time
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
